import type { NewsItem, Video, VideoType } from '@/types';
import { videos } from '@/mocks/videos';
import { newsList } from '@/mocks/news';

export function getVideosByType(type: string): Video[] {
  return videos.filter((v) => v.type === type);
}

export function getVideoById(id: string): Video | undefined {
  return videos.find((v) => v.id === id);
}

export function getHotVideos(limit = 8): Video[] {
  return [...videos].filter((v) => v.isHot).slice(0, limit);
}

export function getLatestVideos(limit = 8): Video[] {
  return [...videos]
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, limit);
}

export function getGenresByType(type: string): string[] {
  const set = new Set<string>();
  getVideosByType(type).forEach((v) => v.genres.forEach((g) => set.add(g)));
  return Array.from(set);
}

export function getYears(): number[] {
  const set = new Set<number>();
  videos.forEach((v) => set.add(v.year));
  return Array.from(set).sort((a, b) => b - a);
}

export function getRelatedVideos(video: Video, limit = 6): Video[] {
  return [...videos]
    .filter((v) => v.id !== video.id)
    .map((v) => {
      let score = 0;
      if (v.type === video.type) score += 2;
      v.genres.forEach((g) => {
        if (video.genres.includes(g)) score += 3;
      });
      if (Math.abs(v.year - video.year) <= 1) score += 1;
      return { v, score };
    })
    .sort((a, b) => b.score - a.score || b.v.rating - a.v.rating)
    .slice(0, limit)
    .map((item) => item.v);
}

export function searchVideos(keyword: string, type?: VideoType | 'all'): Video[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return videos.filter((v) => {
    const matchType = !type || type === 'all' || v.type === type;
    const haystack = [v.title, v.director, ...v.actors, ...v.genres, String(v.year)]
      .join(' ')
      .toLowerCase();
    return matchType && haystack.includes(kw);
  });
}

export function searchNews(keyword: string): NewsItem[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return newsList.filter((n) =>
    [n.title, n.subtitle, ...n.content].join(' ').toLowerCase().includes(kw),
  );
}

export function getNewsById(id: string): NewsItem | undefined {
  return newsList.find((n) => n.id === id);
}

export function getRelatedNews(item: NewsItem, limit = 4): NewsItem[] {
  return [...newsList]
    .filter((n) => n.id !== item.id)
    .map((n) => {
      let score = n.category === item.category ? 2 : 0;
      if (n.title.includes(item.title.slice(0, 3))) score += 1;
      return { n, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((item2) => item2.n);
}

export function getLatestNews(limit = 6): NewsItem[] {
  return [...newsList]
    .sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1))
    .slice(0, limit);
}

export interface HistoryEntry {
  id: string;
  at: number;
}

export function filterHistory(entries: HistoryEntry[]): Video[] {
  return entries
    .map((e) => getVideoById(e.id))
    .filter((v): v is Video => Boolean(v));
}